/**
 * The mask of one scope (S8 Task 8.11, E5 / finding S8-08): its own rwx checkboxes and **Recalculate mask** — the ONLY
 * two ways a mask changes. A loaded mask is kept as loaded while named entries change; recalculating sets it to the
 * union of the named entries and the owning group, which can widen what they may do (the preview marks it).
 */
import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { recalculateMask, setMask } from '../../../../../core/engines/azureStorageAclEffective';
import type { StorageAclEntry, StorageAclPermissions, StorageAclScope } from '../../../../../types/azureStorage';

export const MASK_HINT = 'Named users, named groups and the owning group are limited by the mask.';
const BITS: ReadonlyArray<{ bit: keyof StorageAclPermissions; label: string; letter: 'r' | 'w' | 'x' }> = [{ bit: 'read', label: 'Read', letter: 'r' }, { bit: 'write', label: 'Write', letter: 'w' }, { bit: 'execute', label: 'Execute', letter: 'x' }];

type Props = { entries: StorageAclEntry[]; scope: StorageAclScope; lockedReason?: string; onChange: (next: StorageAclEntry[]) => void; onRecalculate?: () => void; hideRecalculate?: boolean };

export function AclMaskControls({ entries, scope, lockedReason, onChange, onRecalculate, hideRecalculate }: Props) {
  const mask = entries.find((e) => e.scope === scope && e.type === 'mask');
  if (!mask) return null;
  return (
    <div className="grid gap-1 rounded border border-border p-2 text-xs" data-testid={`hns-acl-mask-${scope}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-medium">{scope === 'default' ? 'Default mask' : 'Mask'}</span>
        {BITS.map(({ bit, label, letter }) => (
          <label key={bit} className="flex items-center gap-1">
            <Checkbox checked={mask.permissions[bit]} disabled={!!lockedReason} title={lockedReason} aria-label={`${label} (${scope} mask)`} data-testid={`hns-acl-mask-${scope}-${letter}`}
              onCheckedChange={(checked) => onChange(setMask(entries, scope, { ...mask.permissions, [bit]: checked === true }))} />
            {label}
          </label>
        ))}
        {!hideRecalculate && (
          <Button type="button" variant="outline" size="xs" disabled={!!lockedReason} title={lockedReason ?? 'Set the mask to the union of the named entries and the owning group'} data-testid={`hns-acl-recalculate-mask-${scope}`}
            onClick={() => { onChange(recalculateMask(entries, scope)); onRecalculate?.(); }}>Recalculate mask</Button>
        )}
      </div>
      <p className="text-muted-foreground">{MASK_HINT}</p>
    </div>
  );
}
